/* eslint-disable jsx-a11y/media-has-caption */

import React, { useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import MediaControls from './MediaControls';
import Transcript from './Transcript';

const AudioWrapper = styled.div`
  width: 100%;
  max-width: 700px;
  padding: 0 20px;
  margin: 0 auto 3rem;
  .audio__header {
    font-size: 1rem;
    line-height: 1.25rem;
    color: white;
    span {
      color: var(--custom-red);
      text-transform: uppercase;
    }
  }
`;

const AudioBlock = ({
  data,
  audioData,
  updateAudioData,
  audio,
  enableAudio,
  playing,
  setPlaying,
}) => {
  const audioRef = useRef(null); // audio element
  // index of corresponding obj in audio data
  const audioIndex = useRef(audioData.findIndex((a) => a.id === data.id));
  // const playStateRef = useRef(null); // whether audio is playing
  const progressRef = useRef();
  const transcriptRef = useRef(false);
  const audioWrapperRef = useRef();
  const hasBeenPaused = useRef(false);

  // ---------------------------------------------------------------
  // metaDataLoaded callback
  // ---------------------------------------------------------------
  // runs when the audio file's metadata is loaded
  // updates audio data with duration of audio file

  const parseMetaData = useCallback(() => {
    const clonedAudioData = [...audioData];
    clonedAudioData[audioIndex.current].duration = audioRef.current.duration;
    updateAudioData(clonedAudioData);
  }, [audioData, updateAudioData]);

  // ---------------------------------------------------------------
  // timeUpdate callback
  // ---------------------------------------------------------------
  // runs when the currenttime of the audio changes and updates the audio object
  // with the currentime of the audio file

  const parseTimeUpdate = useCallback(() => {
    const clonedAudioData = [...audioData];
    const { currentTime } = audioRef.current;
    clonedAudioData[audioIndex.current].currentTime = currentTime;
    updateAudioData(clonedAudioData);
    if (audioRef.current.ended) {
      audioRef.current.pause();
      setPlaying('');
      clonedAudioData[audioIndex.current].ended = true;
      updateAudioData(clonedAudioData);
    }
  }, [audioData, updateAudioData]);

  // ---------------------------------------------------------------
  // play/pause audio function
  // ---------------------------------------------------------------
  // controls audio playback and pause depending on the current audio playing state

  const playAudio = useCallback(() => {
    if (playing === '' || playing !== audioData[audioIndex.current].id) {
      audioRef.current.play();
      setPlaying(audioData[audioIndex.current].id);
      hasBeenPaused.current = false;
    } else {
      audioRef.current.pause();
      setPlaying('');
      hasBeenPaused.current = true;
    }
  }, [audioRef.current, playing]);

  useEffect(() => {
    if (playing !== audioData[audioIndex.current].id) {
      audioRef.current.pause();
    }
  }, [playing]);

  // ---------------------------------------------------------------
  // range slider on change callback
  // ---------------------------------------------------------------
  // updates audio file's current time when range slider is changed

  const changeTime = useCallback(() => {
    audioRef.current.currentTime = progressRef.current.value / 1000;
  }, []);

  // ---------------------------------------------------------------
  // transcript toggle
  // ---------------------------------------------------------------
  // updates audio file's transcriptViewable property

  const toggleTranscript = useCallback(() => {
    const clonedAudioData = [...audioData];
    transcriptRef.current = !transcriptRef.current;
    clonedAudioData[audioIndex.current].transcriptViewable = transcriptRef.current;
    updateAudioData(clonedAudioData);
  }, []);

  const inlineAudioToggle = useCallback(() => {
    enableAudio(false);
    setPlaying('');
    audioRef.current.pause();
  }, []);

  useEffect(() => {
    function handleIntersect(entries) {
      entries.forEach((entry) => {
        if (audio && audioData[audioIndex.current].ended !== true) {
          if (entry.isIntersecting && hasBeenPaused.current !== true) {
            audioRef.current.play();
            setPlaying(audioData[audioIndex.current].id);
          } else {
            audioRef.current.pause();
            setPlaying('');
          }
        }
      });
    }

    const options = {
      root: null,
      rootMargin: '0px',
    };

    const observer = new IntersectionObserver(handleIntersect, options);
    if (audioWrapperRef.current) {
      observer.observe(audioWrapperRef.current);
    }
    return () => observer.unobserve(audioWrapperRef.current);
  }, [audioWrapperRef.current, audio, hasBeenPaused.current]);

  // finds the correct audio object and constructs url to where it lives on apps
  // this is extraneous and could just be done by using the data.id
  const audioObj = audioData.find((o) => o.id === data.id);
  const audioPathMP3 = `https://apps.bostonglobe.com/magazine/2022/07/kate-price-audio/${audioObj.id}.mp3`;
  const audioPathOGG = `https://apps.bostonglobe.com/magazine/2022/07/kate-price-audio/${audioObj.id}.ogg`;

  return (
    <AudioWrapper ref={audioWrapperRef}>
      <audio
        ref={audioRef}
        onLoadedMetadata={() => parseMetaData()}
        preload="true"
        onTimeUpdate={() => parseTimeUpdate()}
        autoPlay
      >
        <source src={audioPathMP3} type="audio/mp3" />
        <source src={audioPathOGG} type="audio/ogg" />
      </audio>

      <h6 className="bentonsanscond-n6 audio__header"><span>Listen:</span> {audioObj.description}</h6>

      {audioRef.current !== null && (
        <MediaControls
          playMedia={playAudio}
          maxDuration={audioRef.current.duration}
          currentTime={audioData[audioIndex.current].currentTime}
          changeTime={changeTime}
          playing={playing}
          progressRef={progressRef}
          mediaId={audioData[audioIndex.current].id}
        />
      )}

      <Transcript
        transcriptViewable={audioData[audioIndex.current].transcriptViewable}
        toggleTranscript={toggleTranscript}
        inlineAudioToggle={inlineAudioToggle}
        transcript={audioData[audioIndex.current].transcript}
        audio={audio}
      />
    </AudioWrapper>
  );
};

export default AudioBlock;

AudioBlock.propTypes = {
  data: PropTypes.shape().isRequired,
  audioData: PropTypes.arrayOf(
    PropTypes.shape(),
  ).isRequired,
  updateAudioData: PropTypes.func.isRequired,
  audio: PropTypes.bool.isRequired,
  enableAudio: PropTypes.func.isRequired,
  playing: PropTypes.string.isRequired,
  setPlaying: PropTypes.func.isRequired,
};
